import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Form, Button, Message } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import { FlexRowBetween, PageHeader, LoaderBar } from 'components'
import { AppPrimaryButton } from 'elements'
import { AITemplateService } from 'utility'
import styled from 'styled-components'
import { getRecaptchaToken, ensureRecaptchaLoaded } from 'utility/recaptcha'

const Container = styled.div`
	max-width: 800px;
	margin: 0 auto;
	padding: 20px;
`

const FormContainer = styled.div`
	background: white;
	padding: 30px;
	border-radius: 8px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const StepIndicator = styled.div`
	font-weight: bold;
	font-size: 18px;
	margin-bottom: 20px;
	color: #333;
`

const PopularIndustries = styled.div`
	margin: 20px 0;
`

const IndustryTag = styled.button`
	background: #f0f0f0;
	border: 1px solid #ddd;
	border-radius: 20px;
	padding: 8px 16px;
	margin: 5px;
	cursor: pointer;
	transition: all 0.2s;

	&:hover {
		background: #e0e0e0;
		border-color: #999;
	}

	&.selected {
		background: #007bff;
		color: white;
		border-color: #007bff;
	}
`

const LanguageSelector = styled.div`
	margin: 20px 0;
`

// reCAPTCHA v3 uses invisible token; no widget container needed

export const CreateAITemplateScreen = () => {
	const navigate = useNavigate()
	const routeLocation = useLocation()
	// Only use persisted selections when explicitly coming back from review page
	const fromReview = Boolean(routeLocation.state?.fromReview)
	let persisted = null
	if (fromReview) {
		try {
			persisted = JSON.parse(sessionStorage.getItem('aiTemplateSelections') || 'null')
		} catch (e) {
			// ignore JSON parse/storage errors
		}
	}
	const [loading, setLoading] = useState(false)
	const [generating, setGenerating] = useState(false)
	const [languages, setLanguages] = useState([])
	const [industries, setIndustries] = useState([])
	const [industryOptionsUI, setIndustryOptionsUI] = useState([])
	const [isCustomIndustry, setIsCustomIndustry] = useState(false)
	const [selectedLanguage, setSelectedLanguage] = useState((fromReview && persisted?.language) ? persisted.language : 'en-US')
	const [selectedIndustry, setSelectedIndustry] = useState((fromReview && persisted?.industry) ? persisted.industry : '')
	const [selectedSubcategory, setSelectedSubcategory] = useState((fromReview && persisted?.subcategory) ? persisted.subcategory : '')
	const [questionCount, setQuestionCount] = useState((fromReview && typeof persisted?.questionCount !== 'undefined') ? persisted.questionCount : 10)
	const [subcategories, setSubcategories] = useState([])
	const [error, setError] = useState('')
	const [success, setSuccess] = useState('')
	const [recaptchaToken, setRecaptchaToken] = useState(null)

	const [popularIndustries, setPopularIndustries] = useState([])
	// Let the Dropdown manage open/close itself for better UX after selection
	const subcategoryDropdownRef = useRef(null)
	const subcategoryContainerRef = useRef(null)
	const [highlightSubcategory, setHighlightSubcategory] = useState(false)

	// Helpers: normalize user-added labels and detect duplicates ignoring case/extra spaces
	const normalizeForCompare = value => String(value || '')
		.trim()
		.replace(/\s+/g, ' ')
		.toLowerCase()

	const capitalizeFirstLetter = value => {
		const v = String(value || '').trim().replace(/\s+/g, ' ')
		if (!v) return ''
		return v.charAt(0).toUpperCase() + v.slice(1)
	}

	// For live typing in search input; preserve rest of string
	const capitalizeFirstWordInline = value => {
		const s = String(value || '')
		return s.replace(/^(\s*)([a-zA-Z])/, (_, spaces, ch) => `${spaces}${ch.toUpperCase()}`)
	}

	// Controlled search queries so the add row shows capitalized first letter
	const [industrySearchQuery, setIndustrySearchQuery] = useState('')
	const [subcategorySearchQuery, setSubcategorySearchQuery] = useState('')

	const questionCountOptions = Array.from({ length: 25 }, (_, i) => ({
		key: i + 1,
		text: `${i + 1}`,
		value: i + 1,
	}))

	useEffect(() => {
		loadInitialData()
		// Ensure reCAPTCHA script loads early so the badge shows
		ensureRecaptchaLoaded()
	}, [])

	const toDropdownOption = value => ({ key: value, text: value, value })

	const loadInitialData = async () => {
		setLoading(true)
		try {
			const [languagesData, industriesData] = await Promise.all([
				AITemplateService.getLanguages('R'), // CallFrom: R for AI template creation
				AITemplateService.getIndustriesWithSubcategories(),
			])

			// Languages can be an object map or an array; normalize to dropdown options
			if (languagesData) {
				let languageOptions = []
				if (Array.isArray(languagesData)) {
					languageOptions = languagesData.map(lang => ({
						key: lang.code || lang.Code,
						text: lang.name || lang.Name,
						value: lang.code || lang.Code,
					}))
				} else if (typeof languagesData === 'object') {
					languageOptions = Object.entries(languagesData).map(
						([code, label]) => ({ key: code, text: String(label), value: code }),
					)
				}
				setLanguages(languageOptions)
			}

			// Industries API may return an array directly
			if (Array.isArray(industriesData)) {
				setIndustries(industriesData)
				const famousIndustries = industriesData
					.filter(ind => ind.IsFamous === true)
					.map(ind => ind.Name)
				setPopularIndustries(famousIndustries)
				setIndustryOptionsUI(
					industriesData
						.filter(ind => ind.IsFamous !== true)
						.map(ind => toDropdownOption(String(ind.Name || ind.name || ''))),
				)
			} else if (industriesData?.Items) {
				setIndustries(industriesData.Items)
				const famousIndustries = industriesData.Items
					.filter(ind => ind.IsFamous === true)
					.map(ind => ind.Name)
				setPopularIndustries(famousIndustries)
				setIndustryOptionsUI(
					industriesData.Items
						.filter(ind => ind.IsFamous !== true)
						.map(ind => toDropdownOption(String(ind.Name || ind.name || ''))),
				)
			}

			// Only preload subcategories when coming back from review
			const initialIndustry = fromReview ? persisted?.industry : null
			if (initialIndustry) {
				const found = (industriesData?.Items || industriesData || []).find(ind =>
					ind?.Name === initialIndustry || ind?.name === initialIndustry || ind?.Code === initialIndustry,
				)
				if (found?.Subcategories || found?.subcategories) {
					const subcats = (found.Subcategories || found.subcategories).map(sub => ({
						key: sub.SubcategoryId || sub.Id || sub.Code || sub.Name,
						text: sub.Name || sub.name,
						value: (sub.Name || sub.name || '').toString(),
					}))
					setSubcategories(subcats)
				}
			}

			// Ensure selected industry (including popular ones) appears in dropdown options on return
			if (selectedIndustry) {
				setIndustryOptionsUI(prev => (
					prev.some(opt => opt.value === String(selectedIndustry))
						? prev
						: [...prev, toDropdownOption(String(selectedIndustry))]
				))
			}
		} catch (error) {
			console.error('Error loading initial data:', error)
			setError('Failed to load initial data. Please try again.')
		} finally {
			setLoading(false)
		}
	}

	const handleIndustryChange = industry => {
		setSelectedIndustry(industry)
		setSelectedSubcategory('')
		// Clear search query to show full selected value instead of typed text
		setIndustrySearchQuery('')

		// Determine if selected industry is custom (not in API list)
		const industryData = industries.find(ind =>
			ind?.Name === industry || ind?.name === industry || ind?.Code === industry,
		)
		setIsCustomIndustry(!industryData)

		if (industryData?.Subcategories || industryData?.subcategories) {
			const subcats = (industryData.Subcategories || industryData.subcategories).map(sub => ({
				key: sub.SubcategoryId || sub.Id || sub.Code || sub.Name,
				text: sub.Name || sub.name,
				value: (sub.Name || sub.name || '').toString(),
			}))
			setSubcategories(subcats)
			// Smooth scroll and temporary highlight to instruct user (no forced open)
			setTimeout(() => {
				if (subcategoryContainerRef.current && subcategoryContainerRef.current.scrollIntoView) {
					subcategoryContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
				}
				setHighlightSubcategory(true)
				setTimeout(() => setHighlightSubcategory(false), 1200)
			}, 100)
		} else {
			setSubcategories([])
		}
	}

	const handlePopularIndustryClick = industry => {
		// Ensure the popular industry exists in dropdown options so it is visible as selected
		if (!industryOptionsUI.some(opt => opt.value === String(industry))) {
			setIndustryOptionsUI(prev => [...prev, toDropdownOption(String(industry))])
		}
		// Clear search query to show full selected value
		setIndustrySearchQuery('')
		setSelectedIndustry(industry)
		handleIndustryChange(industry)
	}

	const handleAddIndustry = value => {
		const formatted = capitalizeFirstLetter(value)
		const compareVal = normalizeForCompare(formatted)

		// Check against dropdown options, popular industries, and API industries (all case-insensitive)
		const existsInDropdown = industryOptionsUI.some(
			opt => normalizeForCompare(opt.value) === compareVal,
		)
		const existsInPopular = popularIndustries.some(
			name => normalizeForCompare(name) === compareVal,
		)
		const existsInApi = industries.some(ind =>
			normalizeForCompare(ind?.Name || ind?.name || ind?.Code) === compareVal,
		)

		if (existsInDropdown || existsInPopular || existsInApi) {
			setError('This category already exists.')
			return
		}

		setIndustryOptionsUI([...industryOptionsUI, toDropdownOption(formatted)])
		setSelectedIndustry(formatted)
		// Clear search query to show full selected value
		setIndustrySearchQuery('')
		setIsCustomIndustry(true)
		setSubcategories([])
		setSelectedSubcategory('')
	}

	const handleAddSubcategory = value => {
		const formatted = capitalizeFirstLetter(value)
		const compareVal = normalizeForCompare(formatted)
		if (subcategories.some(opt => normalizeForCompare(opt.value) === compareVal)) {
			setError('This sub-category already exists.')
			return
		}
		setSubcategories([...subcategories, toDropdownOption(formatted)])
		setSelectedSubcategory(formatted)
		// Clear search query to show full selected value
		setSubcategorySearchQuery('')
	}

	// Persist current selections for back navigation
	useEffect(() => {
		const data = {
			industry: selectedIndustry,
			subcategory: selectedSubcategory,
			language: selectedLanguage,
			questionCount,
		}
		try {
			sessionStorage.setItem('aiTemplateSelections', JSON.stringify(data))
		} catch (e) {
			// ignore storage errors
		}
	}, [selectedIndustry, selectedSubcategory, selectedLanguage, questionCount])

	// Automatically acquire a reCAPTCHA v3 token when needed
	const acquireRecaptchaToken = async () => {
		const token = await getRecaptchaToken('ai_template_generate')
		setRecaptchaToken(token)
		return token
	}

	const handleGenerateTemplate = async () => {
		if (!selectedIndustry) {
			setError('Please select an industry')
			return
		}
		if (!isCustomIndustry && !selectedSubcategory) {
			setError('Please select a subcategory')
			return
		}
		const ensuredToken = recaptchaToken || (await acquireRecaptchaToken())
		if (!ensuredToken) {
			setError('reCAPTCHA verification failed. Please try again.')
			return
		}

		setGenerating(true)
		setError('')
		setSuccess('')

		try {
			const result = await AITemplateService.generateTemplate({
				industry: selectedIndustry,
				subcategory: selectedSubcategory || '',
				questionCount,
				templateTitle: `${selectedIndustry}${selectedSubcategory ? ` - ${selectedSubcategory}` : ''} Audit`,
				language: selectedLanguage,
			}, ensuredToken)

			if (result?.Questions) {
				setSuccess('Template generated successfully!')
				navigate('/app/templates/create-ai-template/review', {
					state: {
						generatedTemplate: result,
						industry: selectedIndustry,
						subcategory: selectedSubcategory,
						questionCount,
						language: selectedLanguage,
					},
				})
			} else {
				setError('Failed to generate template. Please try again.')
			}
		} catch (error) {
			console.error('Error generating template:', error)
			setError('Failed to generate template. Please try again.')
		} finally {
			setGenerating(false)
		}
	}

	return (
		<Container>
			<FlexRowBetween>
				<PageHeader header="AI-Based Template Question Creation" />
				<AppPrimaryButton onClick={() => navigate('/app/templates')}>
					Back
				</AppPrimaryButton>
			</FlexRowBetween>

			<FormContainer>
				<StepIndicator>Step 1: Select Industry or Category</StepIndicator>

				{loading && <LoaderBar content="Loading..." />}

				{error && (
					<Message negative>
						<Message.Header>Error</Message.Header>
						<p>{error}</p>
					</Message>
				)}

				{success && (
					<Message positive>
						<Message.Header>Success</Message.Header>
						<p>{success}</p>
					</Message>
				)}

				<Form>
					<PopularIndustries>
						<strong>Popular Industries:</strong>
						<div style={{ marginTop: '10px' }}>
							{popularIndustries.length > 0 ? (
								popularIndustries.map(industry => (
									<IndustryTag
										key={industry}
										className={
											selectedIndustry === industry
												? 'selected'
												: ''
										}
										onClick={() =>
											!generating && handlePopularIndustryClick(industry)
										}
										style={{
											cursor: generating ? 'not-allowed' : 'pointer',
											opacity: generating ? 0.6 : 1,
										}}
									>
										{industry}
									</IndustryTag>
								))
							) : (
								<div style={{ color: '#666', fontStyle: 'italic' }}>
									No popular industries available
								</div>
							)}
						</div>
					</PopularIndustries>

					<Form.Field>
						<label>Industry or Category</label>
						<Dropdown
							placeholder="Type or select an industry..."
							search
							selection
							allowAdditions
							onAddItem={(_, { value }) => handleAddIndustry(value)}
							options={industryOptionsUI}
							value={selectedIndustry}
							onChange={(_, { value }) => handleIndustryChange(value)}
							searchQuery={industrySearchQuery}
							onSearchChange={(_, { searchQuery }) => setIndustrySearchQuery(capitalizeFirstWordInline(searchQuery))}
							disabled={generating}
							style={{ marginBottom: '10px' }}
						/>
					</Form.Field>

					<StepIndicator style={{ marginTop: '20px' }}>Step 2: Select Sub-Category</StepIndicator>

					<Form.Field
						ref={subcategoryContainerRef}
						style={{
							transition: 'background-color 300ms',
							backgroundColor: highlightSubcategory ? 'rgba(255, 235, 59, 0.25)' : 'transparent',
							borderRadius: highlightSubcategory ? 6 : undefined,
							padding: highlightSubcategory ? '6px' : undefined,
						}}
					>
						<label>Sub-Category</label>
						<Dropdown
							placeholder="Type or select an sub-category..."
							search
							selection
							allowAdditions
							onAddItem={(_, { value }) => handleAddSubcategory(value)}
							options={subcategories}
							value={selectedSubcategory}
							onChange={(_, { value }) => {
								setSelectedSubcategory(value)
								// Clear search query to show full selected value instead of typed text
								setSubcategorySearchQuery('')
							}}
							disabled={!selectedIndustry || generating}
							ref={subcategoryDropdownRef}
							searchQuery={subcategorySearchQuery}
							onSearchChange={(_, { searchQuery }) => setSubcategorySearchQuery(capitalizeFirstWordInline(searchQuery))}
						/>
					</Form.Field>

					<Form.Field>
						<label>Number of Questions</label>
						<Dropdown
							placeholder="Select number of questions"
							selection
							options={questionCountOptions}
							value={questionCount}
							onChange={(_, { value }) => setQuestionCount(value)}
							disabled={generating}
							defaultValue={10}
						/>
					</Form.Field>

					<LanguageSelector>
						<label>Language</label>
						<Dropdown
							placeholder="Select language"
							selection
							options={languages}
							value={selectedLanguage}
							onChange={(_, { value }) => setSelectedLanguage(value)}
							disabled={generating}
							defaultValue="en"
						/>
					</LanguageSelector>

					{/* reCAPTCHA v3 token is acquired automatically on submit */}

					<div style={{ marginTop: '30px' }}>
						<AppPrimaryButton
							size="large"
							fluid
							onClick={handleGenerateTemplate}
							loading={generating}
							disabled={!selectedIndustry || generating}
						>
							Generate AI Template
						</AppPrimaryButton>
					</div>

					<div style={{ textAlign: 'center', marginTop: '20px' }}>
						<Button
							as="a"
							onClick={() => navigate('/app/templates')}
							disabled={generating}
							style={{
								color: generating ? '#ccc' : '#007bff',
								background: 'none',
								border: 'none',
								cursor: generating ? 'not-allowed' : 'pointer',
							}}
						>
							View All Templates
						</Button>
					</div>
				</Form>
			</FormContainer>
		</Container>
	)
}
